import { Button } from '@efexteam/components';
import { Card, CardContent, Skeleton } from '@mui/material';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { uploadIcon } from '../../assets';
import useBoolean from '../../hooks/useBoolean';
import { ICompanyDetails, IFileInformation } from '../../services/auth/types';
import clientSvc from '../../services/client/clientSvc';
import { UploadRequestFiles } from '../../services/dashboard/types';
import {
  convertDateToHumanReadable,
  humanFileSize,
} from '../../utils/formater';
import { selectSelectedLanguage } from '../store/language/selectors';

interface ICompanyFiles {
  fileInfo?: IFileInformation;
  company?: ICompanyDetails;
  loading: boolean;
  onSubmit: (data: IFileInformation, blockEdited: 'fileInfo') => void;
  editable?: boolean;
}

const CompanyFiles = ({
  fileInfo,
  company,
  loading,
  onSubmit,
  editable = false,
}: ICompanyFiles) => {
  const { t } = useTranslation();
  const selectedLanguage = useSelector(selectSelectedLanguage);
  const [isEditActive, { toggle: toggleIsEditState }] = useBoolean(false);
  const [form, setForm] = useState({
    generalDocument: '',
    generalDocumentBack: '',
    einDocument: '',
    serviceDocument: '',
  });
  const [fileData, setFileData] = useState<File | null>(null);
  const [fileDataBack, setFileDataBack] = useState<File | null>(null);
  const [fileDataEin, setFileDataEin] = useState<File | null>(null);
  const [fileDataService, setFileDataService] = useState<File | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);

  const isLocalEnv = import.meta.env.VITE_REACT_APP_ENV === 'local';
  const baseUrl = isLocalEnv ? import.meta.env.VITE_EFEX_BASE_URL : '';

  const generalDocumentRef = useRef<any>(null);
  const generalDocumentBackRef = useRef<any>(null);
  const einDocumentRef = useRef<any>(null);
  const serviceDocumentRef = useRef<any>(null);

  const handleFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files, id } = event.target;
    if (!files) return null;
    setForm((prev) => ({
      ...prev,
      [id as unknown as keyof IFileInformation]: files[0],
    }));
    if (files && files.length) {
      if (id === 'generalDocument') setFileData(files[0] as File);
      if (id === 'generalDocumentBack') setFileDataBack(files[0] as File);
      if (id === 'einDocument') setFileDataEin(files[0] as File);
      if (id === 'serviceDocument') setFileDataService(files[0] as File);
      return null;
    }
    setFileData(null);
    return null;
  };

  const uploadFile = async () => {
    try {
      const data = await clientSvc.postUploadKybDocument(
        form as unknown as UploadRequestFiles
      );
      if (data) onSubmit({} as unknown as IFileInformation, 'fileInfo');
    } catch (reqError: any) {
      setError(reqError.msg);
    }
  };

  const handleEdit = () => {
    if (isEditActive && form) {
      uploadFile();
    }
    toggleIsEditState();
  };

  const openFile = (filePath: string) => {
    const newPath = `${baseUrl}${filePath}`;
    window.open(newPath);
  };

  if (loading) {
    return <Skeleton variant="rectangular" height={100} />;
  }

  return (
    <Card className="my-4" title={t('companyFilesLinked') ?? ''}>
      <CardContent>
        <div className="flex flex-wrap justify-between">
          <div className="w-full md:w-1/2 px-1 mb-4">
            <p className="text-xs text-label-light">
              {company?.country === 'USA'
                ? t('generalDocument')
                : t('ineFront')}
            </p>
            {error && <div className="bg-error">{error}</div>}
            {!isEditActive && fileInfo?.generalDocument ? (
              <Button
                appearance="primary"
                className="truncate w-full"
                onClick={() => openFile(fileInfo.generalDocument?.url ?? '')}
              >
                {fileInfo.generalDocument.name.split('/').pop()}
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  appearance="primary"
                  variant="text"
                  endIcon={
                    <img
                      className="w-[15px] mr-2"
                      src={uploadIcon}
                      alt="upload"
                    />
                  }
                  onClick={() => generalDocumentRef?.current?.click()}
                >
                  {company?.country === 'USA'
                    ? t('generalDocument')
                    : t('ineFront')}
                  <input
                    ref={generalDocumentRef}
                    id="generalDocument"
                    className="uploadInvoiceButton"
                    onChange={handleFiles}
                    type="file"
                    hidden
                  />
                </Button>
                {fileData && (
                  <div className="bg-slate-50 rounded p-3 text-primary-text text-left">
                    <h6 className="">{fileData.name}</h6>
                    <p className="text-[0.8rem] m-0">
                      {t('uploadInvoiceModificationDate')}:{' '}
                      <strong>
                        {convertDateToHumanReadable(
                          new Date(fileData.lastModified).toISOString(),
                          selectedLanguage.language.toLowerCase()
                        )}
                      </strong>
                      <br />
                      {t('uploadInvoiceFileSize')}:{' '}
                      <strong>{humanFileSize(fileData.size)}</strong>
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="w-full md:w-1/2 px-1 mb-4">
            <p className="text-xs text-label-light">{t('ineBack')}</p>
            {!isEditActive && fileInfo?.generalDocumentBack ? (
              <Button
                appearance="primary"
                className="truncate w-full"
                onClick={() =>
                  openFile(fileInfo.generalDocumentBack?.url || '')
                }
              >
                {fileInfo.generalDocumentBack.name.split('/').pop()}
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  appearance="primary"
                  variant="text"
                  endIcon={
                    <img
                      className="w-[15px] mr-2"
                      src={uploadIcon}
                      alt="upload"
                    />
                  }
                  onClick={() => generalDocumentBackRef?.current?.click()}
                >
                  {t('ineBack')}
                  <input
                    ref={generalDocumentBackRef}
                    id="generalDocumentBack"
                    className="uploadInvoiceButton"
                    onChange={handleFiles}
                    type="file"
                    hidden
                  />
                </Button>
                {fileDataBack && (
                  <div className="bg-slate-50 rounded p-3 text-primary-text text-left">
                    <h6 className="">{fileDataBack.name}</h6>
                    <p className="text-[0.8rem] m-0">
                      {t('uploadInvoiceModificationDate')}:{' '}
                      <strong>
                        {convertDateToHumanReadable(
                          new Date(fileDataBack.lastModified).toISOString(),
                          selectedLanguage.language.toLowerCase()
                        )}
                      </strong>
                      <br />
                      {t('uploadInvoiceFileSize')}:{' '}
                      <strong>{humanFileSize(fileDataBack.size)}</strong>
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="w-full md:w-1/2 px-1 mb-4 !hidden">
            <p className="text-xs text-label-light">{t('einDocument')}</p>
            {!isEditActive && fileInfo?.einDocument ? (
              <Button
                appearance="primary"
                className="truncate w-full"
                onClick={() => openFile(fileInfo.einDocument?.url ?? '')}
              >
                {fileInfo.einDocument.name.split('/').pop()}
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  appearance="primary"
                  variant="text"
                  endIcon={
                    <img
                      className="w-[15px] mr-2"
                      src={uploadIcon}
                      alt="upload"
                    />
                  }
                  onClick={() => einDocumentRef?.current?.click()}
                >
                  {t('einDocument')}
                  <input
                    ref={einDocumentRef}
                    id="einDocument"
                    className="uploadInvoiceButton"
                    onChange={handleFiles}
                    type="file"
                    hidden
                  />
                </Button>
                {fileDataEin && (
                  <div className="bg-slate-50 rounded p-3 text-primary-text text-left">
                    <h6 className="">{fileDataEin.name}</h6>
                    <p className="text-[0.8rem] m-0">
                      {t('uploadInvoiceModificationDate')}:{' '}
                      <strong>
                        {convertDateToHumanReadable(
                          new Date(fileDataEin.lastModified).toISOString(),
                          selectedLanguage.language.toLowerCase()
                        )}
                      </strong>
                      <br />
                      {t('uploadInvoiceFileSize')}:{' '}
                      <strong>{humanFileSize(fileDataEin.size)}</strong>
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="w-full md:w-1/2 px-1 mb-4">
            <p className="text-xs text-label-light">{t('formStepOneThird')}</p>
            {!isEditActive && fileInfo?.serviceDocument ? (
              <Button
                appearance="primary"
                className="truncate w-full"
                onClick={() => openFile(fileInfo.serviceDocument?.url ?? '')}
              >
                {fileInfo.serviceDocument.name.split('/').pop()}
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  appearance="primary"
                  variant="text"
                  endIcon={
                    <img
                      className="w-[15px] mr-2"
                      src={uploadIcon}
                      alt="upload"
                    />
                  }
                  disabled={!isEditActive}
                  onClick={() => serviceDocumentRef?.current?.click()}
                >
                  {t('formStepOneThird')}
                  <input
                    ref={serviceDocumentRef}
                    id="serviceDocument"
                    className="uploadInvoiceButton"
                    onChange={handleFiles}
                    type="file"
                    hidden
                  />
                </Button>
                {fileDataService && isEditActive && (
                  <div className="bg-slate-50 rounded p-3 text-primary-text text-left">
                    <h6 className="">{fileDataService.name}</h6>
                    <p className="text-[0.8rem] m-0">
                      {t('uploadInvoiceModificationDate')}:{' '}
                      <strong>
                        {convertDateToHumanReadable(
                          new Date(fileDataService.lastModified).toISOString(),
                          selectedLanguage.language.toLowerCase()
                        )}
                      </strong>
                      <br />
                      {t('uploadInvoiceFileSize')}:{' '}
                      <strong>{humanFileSize(fileDataService.size)}</strong>
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="w-full md:w-full text-center">
            {editable && (
              <Button
                onClick={handleEdit}
                className="w-full md:max-w-[150px] text-sm mt-2 mx-3"
                appearance="secondary"
              >
                {isEditActive ? t('update') : t('edit')}
              </Button>
            )}
            {isEditActive && (
              <Button
                onClick={toggleIsEditState}
                className="w-full md:max-w-[150px] text-sm mt-2"
                appearance="primary"
              >
                {t('close')}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
export default CompanyFiles;
